import { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ShoppingCart, Clock, ChefHat, Lightbulb, CheckCircle2, Calendar } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useMushrooms } from "../context/MushroomsContext";
import PreOrderModal from "../components/PreOrderModal";
import RecipeDetailModal from "../components/RecipeDetailModal";
import { useLang } from "../context/LanguageContext";

function BadgeChip({ badge }) {
  if (!badge) return null;
  const key = badge.toLowerCase().replace(/\s+/g, '-').replace("'", "");
  return <span className={`text-xs font-semibold px-3 py-1 rounded-full badge-${key}`}>{badge}</span>;
}

const difficultyColor = {
  Easy: "text-emerald-600",
  Medium: "text-amber-600",
  Hard: "text-red-600",
};

export default function MushroomDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { mushrooms } = useMushrooms();
  const [showPreOrder, setShowPreOrder] = useState(false);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const { t, lang } = useLang();

  const mushroom = mushrooms.find(m => m.id === id);
  const related = mushrooms.filter(m => m.id !== id && m.category === mushroom?.category).slice(0, 3);

  if (!mushroom) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center pt-20" style={{ backgroundColor: 'var(--c-bg)' }}>
        <p className="text-lg mb-4" style={{ color: 'var(--c-muted)' }}>Mushroom not found.</p>
        <Link to="/shop" className="text-sm hover:underline" style={{ color: '#D97A46' }}>Back to Shop</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 pb-24 lg:pb-0" style={{ backgroundColor: 'var(--c-bg)' }}>

      {/* Back */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-6">
        <button data-testid="back-to-shop-btn" onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-sm font-medium transition-colors group hover:text-[#D97A46]" style={{ color: 'var(--c-muted)' }}>
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" /> {t('detail.back')}
        </button>
      </div>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-12 items-start">

          {/* Image */}
          <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }}
            className="relative rounded-3xl overflow-hidden aspect-square max-w-lg mx-auto w-full"
            style={{ backgroundColor: 'var(--c-surface-alt)' }}>
            <img src={mushroom.image} alt={mushroom.name} className="w-full h-full object-cover" data-testid="mushroom-detail-image" />
            {mushroom.badge && <div className="absolute top-4 left-4"><BadgeChip badge={mushroom.badge} /></div>}
            {mushroom.isUpcoming && (
              <div className="absolute top-4 right-4">
                <span className="text-xs font-semibold px-3 py-1 rounded-full bg-amber-100 text-amber-700 border border-amber-200 flex items-center gap-1.5">
                  <Clock className="w-3 h-3" /> {t('featured.upcoming')}
                </span>
              </div>
            )}
          </motion.div>

          {/* Info */}
          <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }} className="flex flex-col gap-6">
            <div>
              <p className="text-sm italic mb-1" style={{ color: 'var(--c-muted)' }} data-testid="mushroom-scientific-name">{mushroom.scientificName}</p>
              <h1 className="font-serif text-4xl sm:text-5xl font-bold tracking-tight" style={{ color: 'var(--c-text)' }} data-testid="mushroom-name">{mushroom.name}</h1>
              <p className="text-base mt-3 leading-relaxed" style={{ color: 'var(--c-muted)' }}>
                {(lang === 'hi' && mushroom.long_description_hi) ? mushroom.long_description_hi : mushroom.longDescription}
              </p>
            </div>

            {mushroom.isUpcoming && mushroom.preOrderFrom && (
              <div className="flex items-center gap-2 p-3 rounded-xl border border-amber-200 bg-amber-50">
                <Calendar className="w-4 h-4 text-amber-600 flex-shrink-0" />
                <p className="text-xs text-amber-700">
                  {t('detail.preOrderWindow', { from: new Date(mushroom.preOrderFrom).toLocaleDateString(), until: mushroom.preOrderUntil ? new Date(mushroom.preOrderUntil).toLocaleDateString() : t('detail.limitedTime') })}
                </p>
              </div>
            )}

            {/* Price & CTA */}
            <div className="p-5 rounded-2xl border" style={{ backgroundColor: 'var(--c-surface)', borderColor: 'var(--c-border)', boxShadow: 'var(--c-shadow-md)' }}>
              <div className="flex items-baseline gap-2 mb-4">
                <span className="font-serif text-3xl font-bold" style={{ color: '#D97A46' }} data-testid="mushroom-price">Rs.{mushroom.price}</span>
                <span className="text-sm" style={{ color: 'var(--c-muted)' }}>{t('detail.perUom', { uom: mushroom.uom || "250g" })} · {t('detail.certOrganic')}</span>
                {mushroom.qty <= 10 && !mushroom.isUpcoming && <span className="text-xs text-red-500 ml-2">{t('detail.onlyLeft', { n: mushroom.qty })}</span>}
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                {!mushroom.isUpcoming ? (
                  <button data-testid="detail-add-to-cart-btn" onClick={() => addToCart(mushroom)}
                    className="flex-1 flex items-center justify-center gap-2 py-3 rounded-full font-semibold text-white transition-all hover:-translate-y-0.5 hover:shadow-md"
                    style={{ backgroundColor: '#D97A46' }}>
                    <ShoppingCart className="w-4 h-4" /> {t('detail.addToCart')}
                  </button>
                ) : (
                  <button data-testid="pre-order-btn" onClick={() => setShowPreOrder(true)}
                    className="flex-1 flex items-center justify-center gap-2 py-3 rounded-full font-semibold text-white transition-all hover:-translate-y-0.5 hover:shadow-md bg-amber-500 hover:bg-amber-600">
                    <Calendar className="w-4 h-4" /> {t('detail.preOrder')}
                  </button>
                )}
              </div>
            </div>

            {/* Chef's Tip */}
            {mushroom.cookingTip && (
              <div className="flex gap-3 p-4 rounded-xl border border-amber-200 bg-amber-50">
                <Lightbulb className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs font-semibold text-amber-700 mb-1">{t('detail.chefTip')}</p>
                  <p className="text-sm" style={{ color: 'var(--c-muted)' }}>{mushroom.cookingTip}</p>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Health Benefits */}
      {mushroom.benefits?.length > 0 && (
        <section className="py-14 px-4 sm:px-6 border-t" style={{ backgroundColor: 'var(--c-surface-alt)', borderColor: 'var(--c-border)' }}>
          <div className="max-w-7xl mx-auto">
            <h2 className="font-serif text-3xl sm:text-4xl font-bold mb-8" style={{ color: 'var(--c-text)' }} data-testid="benefits-heading">{t('detail.healthBenefits')}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {mushroom.benefits.map((b, i) => (
                <motion.div key={b.title} initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                  className="p-5 rounded-2xl border" style={{ backgroundColor: 'var(--c-surface)', borderColor: 'var(--c-border)', boxShadow: 'var(--c-shadow-sm)' }}
                  data-testid={`benefit-${i}`}>
                  <CheckCircle2 className="w-5 h-5 mb-3" style={{ color: '#D97A46' }} />
                  <h3 className="font-semibold text-sm mb-1" style={{ color: 'var(--c-text)' }}>{b.title}</h3>
                  <p className="text-xs leading-relaxed" style={{ color: 'var(--c-muted)' }}>{b.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Uses */}
      {mushroom.uses?.length > 0 && (
        <section className="py-14 px-4 sm:px-6 border-t" style={{ backgroundColor: 'var(--c-bg)', borderColor: 'var(--c-border)' }}>
          <div className="max-w-7xl mx-auto">
            <h2 className="font-serif text-3xl sm:text-4xl font-bold mb-8" style={{ color: 'var(--c-text)' }} data-testid="uses-heading">{t('detail.bestUses')}</h2>
            <div className="flex flex-wrap gap-3">
              {mushroom.uses.map((use, i) => (
                <motion.span key={use} initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                  className="px-4 py-2 rounded-full border text-sm font-medium transition-all hover:border-[#D97A46]/40 hover:text-[#D97A46]"
                  style={{ backgroundColor: 'var(--c-surface)', borderColor: 'var(--c-border)', color: 'var(--c-body)' }}
                  data-testid={`use-tag-${i}`}>
                  {use}
                </motion.span>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Recipes */}
      {mushroom.recipes?.length > 0 && (
        <section className="py-14 px-4 sm:px-6 border-t" style={{ backgroundColor: 'var(--c-surface-alt)', borderColor: 'var(--c-border)' }}>
          <div className="max-w-7xl mx-auto">
            <h2 className="font-serif text-3xl sm:text-4xl font-bold mb-8" style={{ color: 'var(--c-text)' }} data-testid="recipes-heading">{t('detail.recipeIdeas')}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {mushroom.recipes.map((recipe, i) => (
                <motion.div key={recipe.name} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                  className="rounded-2xl overflow-hidden group cursor-pointer hover:-translate-y-1 transition-transform duration-300 border"
                  style={{ backgroundColor: 'var(--c-surface)', borderColor: 'var(--c-border)', boxShadow: 'var(--c-shadow-md)' }}
                  data-testid={`recipe-${i}`} onClick={() => setSelectedRecipe(recipe)}>
                  <div className="relative h-44 overflow-hidden">
                    <img src={recipe.image} alt={recipe.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(26,47,36,0.8) 0%, transparent 60%)' }} />
                    <div className="absolute bottom-3 left-3 flex gap-2">
                      <span className="flex items-center gap-1 text-[10px] bg-black/40 text-white px-2 py-0.5 rounded-full"><Clock className="w-3 h-3" />{recipe.time}</span>
                      <span className={`text-[10px] font-medium ${difficultyColor[recipe.difficulty] || 'text-white'}`}>{recipe.difficulty}</span>
                    </div>
                    {(recipe.ingredients?.length > 0 || recipe.steps?.length > 0) && (
                      <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
                        <span className="text-[10px] text-white px-2 py-0.5 rounded-full" style={{ backgroundColor: '#D97A46' }}>{t('detail.viewRecipe')}</span>
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="font-serif text-lg font-semibold mb-1" style={{ color: 'var(--c-text)' }}>{recipe.name}</h3>
                    <p className="text-xs" style={{ color: 'var(--c-muted)' }}>{recipe.description}</p>
                    {(recipe.ingredients?.length > 0 || recipe.steps?.length > 0) && (
                      <p className="text-xs mt-2" style={{ color: '#D97A46' }}>
                        {recipe.ingredients?.length ? `${recipe.ingredients.length} ingredients` : ''}{recipe.steps?.length ? ` · ${recipe.steps.length} steps` : ''}
                      </p>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA + Related */}
      <section className="py-14 px-4 sm:px-6 border-t" style={{ backgroundColor: 'var(--c-bg)', borderColor: 'var(--c-border)' }}>
        <div className="max-w-7xl mx-auto">
          <div className="mb-14 p-8 rounded-3xl border text-center" style={{ backgroundColor: 'var(--c-surface)', borderColor: 'var(--c-border)', boxShadow: '0 2px 16px rgba(26,47,36,0.06)' }}>
            <ChefHat className="w-10 h-10 mx-auto mb-4" style={{ color: '#D97A46' }} />
            <h3 className="font-serif text-2xl font-bold mb-2" style={{ color: 'var(--c-text)' }}>{t('detail.readyToCook', { name: mushroom.name })}</h3>
            <p className="text-sm mb-6" style={{ color: 'var(--c-muted)' }}>{t('detail.orderFresh')}</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              {!mushroom.isUpcoming ? (
                <button data-testid="cta-add-to-cart-btn" onClick={() => addToCart(mushroom)}
                  className="flex items-center justify-center gap-2 px-8 py-3 rounded-full font-semibold text-white transition-all hover:-translate-y-0.5 hover:shadow-md"
                  style={{ backgroundColor: '#D97A46' }}>
                  <ShoppingCart className="w-4 h-4" /> {t('detail.addToCartPrice', { price: mushroom.price })}
                </button>
              ) : (
                <button data-testid="cta-preorder-btn" onClick={() => setShowPreOrder(true)}
                  className="flex items-center justify-center gap-2 px-8 py-3 rounded-full font-semibold text-white transition-all hover:-translate-y-0.5 hover:shadow-md bg-amber-500 hover:bg-amber-600">
                  <Calendar className="w-4 h-4" /> {t('detail.preOrder')}
                </button>
              )}
            </div>
          </div>

          {related.length > 0 && (
            <div>
              <h2 className="font-serif text-2xl font-bold mb-6" style={{ color: 'var(--c-text)' }}>{t('detail.relatedTitle')}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {related.map(m => (
                  <Link key={m.id} to={`/mushroom/${m.id}`}
                    className="group flex items-center gap-3 p-4 rounded-2xl border transition-all hover:-translate-y-0.5 hover:border-[#D97A46]/30"
                    style={{ backgroundColor: 'var(--c-surface)', borderColor: 'var(--c-border)', boxShadow: 'var(--c-shadow-sm)' }}
                    data-testid={`related-${m.id}`}>
                    <img src={m.image} alt={m.name} className="w-14 h-14 rounded-xl object-cover flex-shrink-0" />
                    <div>
                      <p className="font-medium text-sm group-hover:text-[#D97A46] transition-colors" style={{ color: 'var(--c-text)' }}>{m.name}</p>
                      <p className="text-xs font-semibold mt-0.5" style={{ color: '#D97A46' }}>Rs.{m.price} / {m.uom || "250g"}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {showPreOrder && <PreOrderModal mushroom={mushroom} onClose={() => setShowPreOrder(false)} />}
      {selectedRecipe && <RecipeDetailModal recipe={selectedRecipe} mushroomName={mushroom.name} onClose={() => setSelectedRecipe(null)} />}

      {/* ── Sticky mobile CTA bar ── */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 border-t px-4 py-3 flex items-center gap-3"
        style={{ backgroundColor: 'var(--c-surface)', borderColor: 'var(--c-border)', backdropFilter: 'blur(12px)' }}>
        <div className="flex-1">
          <p className="text-xs" style={{ color: 'var(--c-muted)' }}>{mushroom.name}</p>
          <p className="text-base font-bold" style={{ color: '#D97A46' }}>Rs.{mushroom.price} <span className="text-xs font-normal" style={{ color: 'var(--c-muted)' }}>/ {mushroom.uom || '250g'}</span></p>
        </div>
        {!mushroom.isUpcoming ? (
          <button data-testid="sticky-add-to-cart-btn" onClick={() => addToCart(mushroom)}
            className="flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-white text-sm transition-all active:scale-95"
            style={{ backgroundColor: '#D97A46' }}>
            <ShoppingCart className="w-4 h-4" /> {t('detail.addToCart')}
          </button>
        ) : (
          <button data-testid="sticky-preorder-btn" onClick={() => setShowPreOrder(true)}
            className="flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-white text-sm bg-amber-500 active:scale-95">
            <Calendar className="w-4 h-4" /> {t('detail.preOrder')}
          </button>
        )}
      </div>
    </div>
  );
}
